<script setup lang="ts">
/**
 * Corporate Shareholder Profile (CDD) — 디렉터/주주 행은 + 로 N개.
 */
import { inject, onMounted, ref, watch } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import { DOC_SCOPE, useCddFill } from './cdd/useCddFill';
import './allotDoc.css';

const fill = useCddFill();
const scope = inject(DOC_SCOPE, '');

const dirRows = ref<number[]>([1]);
const shRows = ref<number[]>([1]);
let dirSeq = 1;
let shSeq = 1;

function addDir() {
  dirSeq += 1;
  dirRows.value.push(dirSeq);
}
function removeDir(id: number) {
  if (dirRows.value.length <= 1) return;
  dirRows.value = dirRows.value.filter((r) => r !== id);
}
function addSh() {
  shSeq += 1;
  shRows.value.push(shSeq);
}
function removeSh(id: number) {
  if (shRows.value.length <= 1) return;
  shRows.value = shRows.value.filter((r) => r !== id);
}

/** Autofill 값이 scope 아래에 있으면 행 개수를 맞춤 (빈 키는 무시 → 축소 가능) */
function syncRowsFromValues() {
  const prefix = scope || '';
  const dirIds = new Set<number>();
  const shIds = new Set<number>();
  for (const k of Object.keys(fill.values)) {
    if (prefix && !k.startsWith(prefix)) continue;
    if (!String(fill.values[k] || '').trim()) continue;
    const bare = prefix ? k.slice(prefix.length) : k;
    const dm = /^d(\d+)_/.exec(bare);
    if (dm) dirIds.add(Number(dm[1]));
    const sm = /^s(\d+)_/.exec(bare);
    if (sm) shIds.add(Number(sm[1]));
  }
  const nextDirs = dirIds.size ? [...dirIds].sort((a, b) => a - b) : [1];
  const nextShs = shIds.size ? [...shIds].sort((a, b) => a - b) : [1];
  // 줄어든 id 잔여 값 제거
  for (const id of dirRows.value) {
    if (!nextDirs.includes(id)) {
      const re = new RegExp(`^${prefix}d${id}_`);
      for (const k of Object.keys(fill.values)) {
        if (re.test(k)) fill.setValue(k, '');
      }
    }
  }
  for (const id of shRows.value) {
    if (!nextShs.includes(id)) {
      const re = new RegExp(`^${prefix}s${id}_`);
      for (const k of Object.keys(fill.values)) {
        if (re.test(k)) fill.setValue(k, '');
      }
    }
  }
  dirRows.value = nextDirs;
  shRows.value = nextShs;
  dirSeq = Math.max(...dirRows.value, dirSeq);
  shSeq = Math.max(...shRows.value, shSeq);
}

onMounted(() => syncRowsFromValues());
watch(
  () => Object.keys(fill.values).filter((k) => !scope || k.startsWith(scope)).join('|'),
  () => syncRowsFromValues(),
);

defineExpose({ syncRowsFromValues });
</script>

<template>
  <div class="allot-doc">
    <div class="doc-head">
      <div class="doc-lbl-title">CORPORATE SHAREHOLDER PROFILE</div>
      <div class="doc-meta">(Customer Due Diligence)</div>
      <div class="doc-meta" style="margin-top:6px">
        <!-- Aura 전용 — CSP 는 항상 AURA PARTNERS 고정(문서엔 csp 변수 없음). -->
        CSP: <span class="sg-csp-fixed">AURA PARTNERS (SINGAPORE) PTE. LTD.</span>
      </div>
    </div>

    <p class="doc-sec-title">Proposed / Client Company</p>
    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">Entity / Company Name</td>
          <td><CddVarField field-key="company_name" label="Company Name" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">UEN / Registration No.</td>
          <td><CddVarField field-key="company_registration_no" label="Company Registration No." /></td>
        </tr>
      </tbody>
    </table>

    <p class="doc-sec-title">1. Company Information</p>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Company Name</td><td><CddVarField field-key="cs_company_name" label="Company Name" /></td></tr>
        <tr><td class="cdd-lcell">Company Registration No.</td><td><CddVarField field-key="cs_registration_no" label="Registration No." /></td></tr>
        <tr><td class="cdd-lcell">Former Name (if any)</td><td><CddVarField field-key="cs_former_name" label="Former Name" /></td></tr>
        <tr><td class="cdd-lcell">Country of Incorporation</td><td><CddVarField field-key="cs_country_of_incorporation" label="Country of Incorporation" /></td></tr>
        <tr><td class="cdd-lcell">Date of Incorporation</td><td><CddVarField field-key="cs_incorporation_date" label="Date of Incorporation" /></td></tr>
        <tr><td class="cdd-lcell">Business Activity</td><td><CddVarField field-key="cs_business_activity" label="Business Activity" /></td></tr>
        <tr><td class="cdd-lcell">Paid-Up Capital (with currency)</td><td><CddVarField field-key="cs_paid_up_capital" label="Paid-Up Capital" /></td></tr>
        <tr><td class="cdd-lcell">No. of Shares Issued</td><td><CddVarField field-key="cs_no_of_shares_issued" label="No. of Shares Issued" /></td></tr>
        <tr><td class="cdd-lcell">Registered Address</td><td><CddVarField field-key="cs_registered_address" label="Registered Address" /></td></tr>
        <tr><td class="cdd-lcell">Last Financial Year</td><td><CddVarField field-key="cs_last_financial_year" label="Last Financial Year" /></td></tr>
        <tr><td class="cdd-lcell">Website</td><td><CddVarField field-key="cs_website" label="Website" /></td></tr>
        <tr>
          <td class="cdd-lcell">Publicly Listed?</td>
          <td>
            <CddVarField field-key="cs_is_public_listed" label="Publicly Listed (Yes/No)" />
          </td>
        </tr>
      </tbody>
    </table>

    <p class="doc-sec-title">2. Contact Information</p>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Contact Person</td><td><CddVarField field-key="cs_contact_person" label="Contact Person" /></td></tr>
        <tr><td class="cdd-lcell">Contact No. (Office)</td><td><CddVarField field-key="cs_contact_no_office" label="Office Contact No." /></td></tr>
        <tr><td class="cdd-lcell">Email</td><td><CddVarField field-key="cs_email" label="Email" /></td></tr>
        <tr><td class="cdd-lcell">Mobile No.</td><td><CddVarField field-key="cs_mobile_no" label="Mobile No." /></td></tr>
      </tbody>
    </table>

    <p class="doc-sec-title">3. Particulars of Director(s)</p>
    <div v-for="(id, i) in dirRows" :key="'d-' + id" class="sg-repeat">
      <div class="sg-repeat-h">
        <span>Director #{{ i + 1 }}</span>
        <button v-if="dirRows.length > 1" type="button" class="sg-x" @click="removeDir(id)">&times;</button>
      </div>
      <table class="cdd-tbl">
        <tbody>
          <tr><td class="cdd-lcell">No.</td><td><CddVarField :field-key="`d${id}_no`" label="No." /></td></tr>
          <tr><td class="cdd-lcell">Name</td><td><CddVarField :field-key="`d${id}_name`" label="Director Name" /></td></tr>
          <tr><td class="cdd-lcell">Nationality</td><td><CddVarField :field-key="`d${id}_nationality`" label="Nationality" /></td></tr>
          <tr><td class="cdd-lcell">ID / Passport No.</td><td><CddVarField :field-key="`d${id}_identification_no`" label="ID / Passport No." /></td></tr>
          <tr><td class="cdd-lcell">Residential Address</td><td><CddVarField :field-key="`d${id}_residential_address`" label="Residential Address" /></td></tr>
          <tr><td class="cdd-lcell">Date of Appointment</td><td><CddVarField :field-key="`d${id}_date_of_appointment`" label="Date of Appointment" /></td></tr>
        </tbody>
      </table>
    </div>
    <div class="sg-add-row">
      <button type="button" class="sg-add" @click="addDir">+ Add Director</button>
    </div>

    <p class="doc-sec-title">4. Particulars of Shareholder(s)</p>
    <div v-for="(id, i) in shRows" :key="'s-' + id" class="sg-repeat">
      <div class="sg-repeat-h">
        <span>Shareholder #{{ i + 1 }}</span>
        <button v-if="shRows.length > 1" type="button" class="sg-x" @click="removeSh(id)">&times;</button>
      </div>
      <table class="cdd-tbl">
        <tbody>
          <tr><td class="cdd-lcell">No.</td><td><CddVarField :field-key="`s${id}_no`" label="No." /></td></tr>
          <tr><td class="cdd-lcell">Name</td><td><CddVarField :field-key="`s${id}_name`" label="Shareholder Name" /></td></tr>
          <tr><td class="cdd-lcell">Nationality</td><td><CddVarField :field-key="`s${id}_nationality`" label="Nationality" /></td></tr>
          <tr><td class="cdd-lcell">ID / Passport No.</td><td><CddVarField :field-key="`s${id}_identification_no`" label="ID / Passport No." /></td></tr>
          <tr><td class="cdd-lcell">Residential Address</td><td><CddVarField :field-key="`s${id}_residential_address`" label="Residential Address" /></td></tr>
          <tr><td class="cdd-lcell">Date of Appointment</td><td><CddVarField :field-key="`s${id}_date_of_appointment`" label="Date of Appointment" /></td></tr>
          <tr><td class="cdd-lcell">No. of Shares / %</td><td><CddVarField :field-key="`s${id}_no_of_shares_and_percentage`" label="Shares / %" /></td></tr>
        </tbody>
      </table>
    </div>
    <div class="sg-add-row">
      <button type="button" class="sg-add" @click="addSh">+ Add Shareholder</button>
    </div>
  </div>
</template>

<style scoped>
.sg-repeat { margin-bottom: 10px; border: 1px dashed #c9d2e3; border-radius: 8px; padding: 8px 10px; }
.sg-repeat-h { display: flex; align-items: center; justify-content: space-between; font-size: 12px; font-weight: 700; color: #42506a; margin-bottom: 6px; }
.sg-x { border: none; background: transparent; color: #c0392b; font-size: 18px; cursor: pointer; line-height: 1; }
.sg-add-row { display: flex; justify-content: flex-end; margin: 4px 0 14px; }
.sg-add { border: 1px solid #1B84FF; background: #eaf3ff; color: #1B84FF; border-radius: 8px; padding: 6px 12px; font-size: 12px; font-weight: 700; cursor: pointer; }
.sg-csp-fixed { font-weight: 700; }
</style>
